import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Http } from '@mygpt/app/http';

interface McpUiFrameProps {
  url?: string;
  srcdoc?: string;
  resourceUri?: string;
  appData?: Record<string, unknown>;
  botId?: string;
  width?: string;
  height?: string;
  onAppResponse?: (payload: unknown) => void;
}

const _mcpAppResourceCache: Record<string, string> = {};

/**
 * Convert a ui:// URI to a static file URL served by /robot-assets/.
 * ui://{server}/{resource} -> /robot-assets/{botId}/skills/{server}/apps/{resource}.html
 */
function resolveResourceUrl(resourceUri: string, botId: string): string | null {
  if (!resourceUri.startsWith('ui://')) return null;
  const rest = resourceUri.slice(5);
  const idx = rest.indexOf('/');
  if (idx < 0) return null;
  const server = rest.slice(0, idx);
  const resource = rest.slice(idx + 1);
  return `/robot-assets/${botId}/skills/${server}/apps/${resource}.html`;
}

async function fetchMcpAppResource(resourceUri: string, botId: string): Promise<string | null> {
  const cacheKey = `${botId}:${resourceUri}`;
  if (_mcpAppResourceCache[cacheKey]) return _mcpAppResourceCache[cacheKey];
  try {
    const url = resolveResourceUrl(resourceUri, botId);
    if (!url) throw new Error(`Cannot resolve URI: ${resourceUri}`);
    const http = Http.use();
    const resp = await http.get<string>(url, {
      responseType: 'text',
      transformResponse: [(data: string) => data],
    });
    const html = resp.data;
    _mcpAppResourceCache[cacheKey] = html;
    return html;
  } catch (e) {
    console.error(`Failed to fetch MCP App resource: ${resourceUri}`, e);
    return null;
  }
}

export const McpUiFrame: React.FC<McpUiFrameProps> = ({
  url,
  srcdoc,
  resourceUri,
  appData,
  botId,
  width = '100%',
  height = '400px',
  onAppResponse,
}) => {
  const [autoHeight, setAutoHeight] = useState<string | null>(null);
  const [fullscreenVisible, setFullscreenVisible] = useState(false);
  const [fetchedSrcdoc, setFetchedSrcdoc] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const fullscreenIframeRef = useRef<HTMLIFrameElement>(null);

  // Fetch resource HTML for standard MCP App mode
  useEffect(() => {
    if (resourceUri && botId && !srcdoc && !url) {
      fetchMcpAppResource(resourceUri, botId).then((html) => {
        if (html) setFetchedSrcdoc(html);
      });
    }
  }, [resourceUri, botId, srcdoc, url]);

  // Send appData via postMessage when iframe is ready
  useEffect(() => {
    if (!appData) return;
    const handleMessage = (event: MessageEvent) => {
      if (event.source === iframeRef.current?.contentWindow) {
        if (event.data?.type === 'mcp-app-ready') {
          iframeRef.current?.contentWindow?.postMessage({ type: 'mcp-app-data', payload: appData }, '*');
        }
        if (event.data?.type === 'mcp-app-response' && onAppResponse) {
          onAppResponse(event.data.payload);
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [appData, onAppResponse]);

  const containerStyle = useMemo<React.CSSProperties>(() => {
    if (!width || width === '100%') return { width: '100%' };
    return { width };
  }, [width]);

  const frameStyle = useMemo<React.CSSProperties>(
    () => ({
      width: '100%',
      height: autoHeight || (height === 'auto' ? '400px' : height || '400px'),
      border: 'none',
      borderRadius: '8px',
    }),
    [autoHeight, height]
  );

  const setupAutoResize = useCallback(
    (iframe: HTMLIFrameElement) => {
      if (height !== 'auto') return;
      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (doc?.documentElement) {
          const updateHeight = () => {
            const h = Math.min(doc.documentElement.scrollHeight, 800);
            if (h > 0) setAutoHeight(h + 'px');
          };
          updateHeight();
          const observer = new MutationObserver(updateHeight);
          observer.observe(doc.body, { childList: true, subtree: true, attributes: true });
        }
      } catch {
        // Cross-origin restriction
      }
    },
    [height]
  );

  const onLoad = useCallback(
    (event: React.SyntheticEvent<HTMLIFrameElement>) => {
      setupAutoResize(event.currentTarget);
    },
    [setupAutoResize]
  );

  const sendFullscreenAppData = useCallback(() => {
    if (!fullscreenIframeRef.current || !appData) return;
    fullscreenIframeRef.current.contentWindow?.postMessage({ type: 'mcp-app-data', payload: appData }, '*');
  }, [appData]);

  // Determine actual srcdoc (direct or fetched)
  const effectiveSrcdoc = srcdoc || fetchedSrcdoc;

  // allow-popups + allow-popups-to-escape-sandbox：允许 MCP 生成的 UI 里 target="_blank" 链接正常打开新标签页，
  // 且新标签页不继承沙箱限制（否则打开的页面脚本会被禁用）
  const iframeProps = url
    ? {
        src: url,
        sandbox: 'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox' as const,
      }
    : {
        srcDoc: effectiveSrcdoc || undefined,
        sandbox: 'allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox' as const,
      };

  const fullscreenIframeProps = iframeProps;

  // Don't render if no content available yet
  if (!url && !effectiveSrcdoc) return null;

  return (
    <>
      <div
        style={{
          margin: '12px 0',
          borderRadius: '8px',
          overflow: 'hidden',
          border: '1px solid var(--mantine-color-gray-3)',
          position: 'relative',
          maxWidth: '100%',
          ...containerStyle,
        }}
      >
        <div style={{ position: 'relative' }} className="group">
          <iframe ref={iframeRef} {...iframeProps} style={frameStyle} onLoad={onLoad} />
          <button
            title="Fullscreen"
            onClick={() => setFullscreenVisible(true)}
            className="opacity-0 group-hover:opacity-100"
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              width: 32,
              height: 32,
              borderRadius: 6,
              border: 'none',
              background: 'rgba(255,255,255,0.9)',
              color: '#333',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'opacity 0.2s ease',
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
              zIndex: 1,
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 3 21 3 21 9" />
              <polyline points="9 21 3 21 3 15" />
              <line x1="21" y1="3" x2="14" y2="10" />
              <line x1="3" y1="21" x2="10" y2="14" />
            </svg>
          </button>
        </div>
      </div>

      {fullscreenVisible &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 9999,
              background: 'rgba(0,0,0,0.6)',
              display: 'flex',
              flexDirection: 'column',
              animation: 'mcpui-fadein 0.2s ease',
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) setFullscreenVisible(false);
            }}
          >
            <style>{`@keyframes mcpui-fadein { from { opacity: 0; } to { opacity: 1; } }`}</style>
            <button
              onClick={() => setFullscreenVisible(false)}
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                border: 'none',
                background: 'rgba(255,255,255,0.15)',
                color: '#fff',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.15s ease',
                position: 'absolute',
                top: 12,
                right: 12,
                zIndex: 10,
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            <iframe
              ref={fullscreenIframeRef}
              {...fullscreenIframeProps}
              onLoad={sendFullscreenAppData}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                border: 'none',
                background: '#fff',
              }}
            />
          </div>,
          document.body
        )}
    </>
  );
};
